import React from 'react';
import { Field } from 'formik';
import ReactQuill from 'react-quill-new';

import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

import { FormikRichTextEditor } from '../../_core/Formik/formik-rich-text-editor.component';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { FormLabel } from '../../_core/_ui/forms.component';
import { IAdHocTokenReport } from '../../../lib/types/communication.types';
import { VariablePanel } from '../VariablePanel/variable-panel.component';
import { InsertTarget } from './compose-panel.insert';
import { TokenHighlightField } from './token-highlight-field.component';
import { QuillTokenHighlightOverlay } from './quill-token-highlight-overlay.component';
import { FieldTokenIssues } from './field-token-issues.component';
import { IFieldTokenMessage } from './compose-panel.validation';
import { messages } from './compose-panel.messages';
import { SectionDivider } from './compose-panel.styles';

interface IComposeEmailSectionProps {
  assessmentId: string;
  hasSessionContext: boolean;
  quillRef: React.RefObject<ReactQuill>;
  onQuillSelectionCapture: (range: ReactQuill.Range) => void;
  onFieldFocus: (target: InsertTarget) => void;
  onInsertVariable: (token: string) => void;
  isSubmitting: boolean;
  // Latest server token report — drives the in-field invalid-token highlight.
  tokenReport?: IAdHocTokenReport;
  // Live per-field issue messages for the subject / email body (undefined = clean).
  subjectIssues?: IFieldTokenMessage[];
  bodyIssues?: IFieldTokenMessage[];
}

export const ComposeEmailSection: React.FC<IComposeEmailSectionProps> = ({
  assessmentId,
  hasSessionContext,
  quillRef,
  onQuillSelectionCapture,
  onFieldFocus,
  onInsertVariable,
  isSubmitting,
  tokenReport,
  subjectIssues,
  bodyIssues
}) => (
  <>
    <SectionDivider />
    <VariablePanel
      sectionLabel={(
        <Typography variant="overline" color="textSecondary">
          <IntlMsg {...messages.emailSectionHeading} />
        </Typography>
      )}
      assessmentId={assessmentId}
      hasSessionContext={hasSessionContext}
      onInsert={onInsertVariable}
    />

    <Field
      fid="cmp"
      name="subject"
      component={TokenHighlightField}
      label="Subject"
      report={tokenReport}
      onFocus={() => onFieldFocus('subject')}
      disabled={isSubmitting}
    />
    <FieldTokenIssues issues={subjectIssues} />

    <Box mt={2} mb={0.5}>
      <FormLabel>Message</FormLabel>
    </Box>
    <Box position="relative">
      <Field
        fid="cmpb"
        name="bodyHtml"
        component={FormikRichTextEditor}
        placeholder="Write your email message…"
        quillRef={quillRef}
        onSelectionCapture={onQuillSelectionCapture}
        onFocus={() => onFieldFocus('bodyHtml')}
        readOnly={isSubmitting}
      />
      <QuillTokenHighlightOverlay quillRef={quillRef} report={tokenReport} />
    </Box>
    <FieldTokenIssues issues={bodyIssues} />
  </>
);
